import { z } from 'zod'

import type {
  DashboardTeamPerformanceRetrieveParams,
  DashboardTeamPerformanceRetrievePeriod,
} from '@gta/api-client'

/**
 * 团队业绩页的 URL 状态契约(唯一真源)。
 *
 * 红线沿用五域:**期间与排序都是「有意义的应用状态」,必须进 search params** ——
 * 冷加载 /team-performance?period=all&ordering=-lead_count 要完整还原视图,
 * 组件里不留 useState 版的期间 / 排序态。非法值一律 `.catch()` 降级到默认档
 * (后端对非法 period 是 400,脏 URL 不该把页面打成错误页)。
 *
 * 与其它域的差别:本页数据一次性下发(员工数量级 ~10,后端零分页),
 * 所以**排序在客户端做**,`ordering` 不下发给后端 —— 只有 `period` 是 query param。
 */

/** 期间取值面直接钉在契约上:多写一档 / 拼错,tsc 立刻报错。 */
export const PERFORMANCE_PERIODS = [
  'month',
  'all',
] as const satisfies readonly DashboardTeamPerformanceRetrievePeriod[]

export type PerformancePeriod = (typeof PERFORMANCE_PERIODS)[number]

export const DEFAULT_PERFORMANCE_PERIOD: PerformancePeriod = 'month'

/** 期间的中文名(切换器与页头共用,避免两处各写一份)。 */
export const PERFORMANCE_PERIOD_LABEL: Record<PerformancePeriod, string> = {
  month: '本月',
  all: '全部',
}

/** 可点排序的列(字段名与 `TeamPerformanceEmployeeRow` 逐一同名)。 */
export type PerformanceSortField =
  | 'display_name'
  | 'team_name'
  | 'lead_count'
  | 'open_count'
  | 'won_count'
  | 'lost_count'
  | 'won_rate_percent'
  | 'contracted_total'

/** 排序取值面的正反序全展开(与其它域一致:`-` 前缀即降序)。 */
export const PERFORMANCE_ORDERING_VALUES = [
  'display_name',
  '-display_name',
  'team_name',
  '-team_name',
  'lead_count',
  '-lead_count',
  'open_count',
  '-open_count',
  'won_count',
  '-won_count',
  'lost_count',
  '-lost_count',
  'won_rate_percent',
  '-won_rate_percent',
  'contracted_total',
  '-contracted_total',
] as const

export type PerformanceOrdering = (typeof PERFORMANCE_ORDERING_VALUES)[number]

/** 默认按签约额降序 —— 销售视角进页面第一眼要看的就是「谁签得多」。 */
export const DEFAULT_PERFORMANCE_ORDERING: PerformanceOrdering = '-contracted_total'

export const teamPerformanceSearchSchema = z.object({
  /** month=本月建档的线索(默认);all=全量 */
  period: z.enum(PERFORMANCE_PERIODS).default(DEFAULT_PERFORMANCE_PERIOD).catch(DEFAULT_PERFORMANCE_PERIOD),
  /** 员工表排序(客户端排序,不下发后端) */
  ordering: z
    .enum(PERFORMANCE_ORDERING_VALUES)
    .default(DEFAULT_PERFORMANCE_ORDERING)
    .catch(DEFAULT_PERFORMANCE_ORDERING),
})

export type TeamPerformanceSearch = z.infer<typeof teamPerformanceSearchSchema>

/** 等于默认值的参数不回写 URL(stripSearchParams,A4 全站统一):裸 /team-performance 即本月档。 */
export const TEAM_PERFORMANCE_SEARCH_DEFAULTS = {
  period: DEFAULT_PERFORMANCE_PERIOD,
  ordering: DEFAULT_PERFORMANCE_ORDERING,
} satisfies Partial<TeamPerformanceSearch>

/** URL search params → 后端 query params(只有 period 下发;排序是纯客户端视图态)。 */
export function toTeamPerformanceParams(
  search: TeamPerformanceSearch,
): DashboardTeamPerformanceRetrieveParams {
  return { period: search.period }
}

/** 名称类字段默认升序(姓名/团队名按字典序读),计数与金额默认降序(先看大的)。 */
function defaultDescending(field: PerformanceSortField): boolean {
  return field !== 'display_name' && field !== 'team_name'
}

/** 点表头排序:同字段反转方向,换字段落到该字段的默认方向。 */
export function nextOrdering(
  current: PerformanceOrdering,
  field: PerformanceSortField,
): PerformanceOrdering {
  if (current === field) return `-${field}` as PerformanceOrdering
  if (current === `-${field}`) return field as PerformanceOrdering
  return (defaultDescending(field) ? `-${field}` : field) as PerformanceOrdering
}

/** 当前排序方向;该字段没在排序中则返回 null(表头显示中性双箭头)。 */
export function sortDirection(
  current: PerformanceOrdering,
  field: PerformanceSortField,
): 'asc' | 'desc' | null {
  if (current === field) return 'asc'
  if (current === `-${field}`) return 'desc'
  return null
}

/** 拆出「按哪个字段、什么方向」——排序实现与表头共用同一份解析。 */
export function parseOrdering(ordering: PerformanceOrdering): {
  field: PerformanceSortField
  descending: boolean
} {
  const descending = ordering.startsWith('-')
  return {
    field: (descending ? ordering.slice(1) : ordering) as PerformanceSortField,
    descending,
  }
}
